import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ChatWidget from '@/components/feature/ChatWidget';
import { companyStats, milestones, coreValues } from '@/mocks/aboutData';

function AnimatedCounter({ target, suffix = '' }: { target: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            const numericValue = parseFloat(target.replace(/[^0-9.]/g, ''));
            const startTime = Date.now();
            const animate = () => {
              const elapsed = Date.now() - startTime;
              const progress = Math.min(elapsed / 2000, 1);
              const easeOut = 1 - Math.pow(1 - progress, 3);
              setCount(Math.floor(numericValue * easeOut));
              if (progress < 1) requestAnimationFrame(animate);
              else setCount(numericValue);
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, hasAnimated]);

  const display = target.includes('+') ? `${count}+` : `${count}`;
  return <div ref={ref}>{hasAnimated ? display : '0'}</div>;
}

export default function AboutPage() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeMilestone, setActiveMilestone] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-firmai-dark" ref={sectionRef}>
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-firmai-darker to-firmai-dark" />
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-firmai-green/8 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-firmai-blue/8 rounded-full blur-3xl" />

          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">关于我们</span>
              <h1 className="font-display font-bold text-3xl md:text-5xl lg:text-6xl text-white mt-4 leading-tight">
                用科技驱动
                <br />
                <span className="bg-gradient-to-r from-firmai-green to-firmai-blue bg-clip-text text-transparent">
                  能源管理变革
                </span>
              </h1>
              <p className="text-firmai-text mt-6 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
                寻御科技是专注工业碳合规与工业AI智能化的一站式服务平台，总部位于上海市虹口区。
                我们致力于工信部碳排放申报、工业AI工厂落地、CCER/CBAM合规填报与数据上报，为B端制造企业、工业园区、外贸出口企业提供工业双碳数字化服务。
              </p>
            </div>

            {/* Stats */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-200 grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-3xl mx-auto">
              {companyStats.map((stat) => (
                <div key={stat.label} className="bg-firmai-card/50 border border-firmai-border rounded-xl p-5 hover:border-firmai-green/30 transition-all duration-300 group">
                  <div className="text-firmai-green font-display font-bold text-2xl md:text-3xl group-hover:scale-105 transition-transform duration-300">
                    <AnimatedCounter target={stat.value} />
                  </div>
                  <div className="text-firmai-muted text-xs md:text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="relative py-16 md:py-24 bg-firmai-darker">
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 max-w-4xl mx-auto text-center">
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white">我们的使命</h2>
              <p className="text-firmai-text mt-6 text-base md:text-lg leading-relaxed">
                我们相信，每一家企业都有责任和能力成为绿色发展的践行者。Firmai 的使命是通过技术创新，
                让能源管理变得简单、智能、高效，帮助企业在降本增效的同时，为地球的可持续未来贡献力量。
                我们的目标是到 2030 年，帮助 10,000 家企业实现碳中和。
              </p>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="relative py-16 md:py-24 bg-firmai-dark">
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-12">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">企业文化</span>
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3">核心价值观</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 max-w-5xl mx-auto">
              {coreValues.map((value, index) => (
                <div
                  key={value.title}
                  className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl p-6 md:p-8 text-center hover:border-firmai-green/30 transition-all duration-300 group"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-gradient-to-br from-firmai-green/20 to-firmai-blue/20 border border-firmai-green/20 mb-4 group-hover:scale-110 transition-transform duration-300">
                    <span className="w-7 h-7 flex items-center justify-center text-firmai-green">
                      <i className={`${value.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-base mb-2">{value.title}</h3>
                  <p className="text-firmai-text text-sm leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="relative py-16 md:py-24 bg-firmai-darker overflow-hidden">
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-12">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">发展历程</span>
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3">从初创到领先</h2>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 relative">
                {/* Timeline line */}
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-firmai-border md:-translate-x-px" />

                <div className="space-y-8">
                  {milestones.map((milestone, index) => {
                    const isLeft = index % 2 === 0;
                    const isActive = activeMilestone === index;
                    return (
                      <div
                        key={milestone.year}
                        className={`relative flex items-start gap-6 md:gap-0 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                        onMouseEnter={() => setActiveMilestone(index)}
                      >
                        {/* Dot */}
                        <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full border-2 z-10 -translate-x-1.5 mt-1.5 transition-all duration-300"
                          style={{
                            borderColor: isActive ? '#00D9A3' : '#2A3441',
                            backgroundColor: isActive ? '#00D9A3' : '#0A1628',
                            boxShadow: isActive ? '0 0 12px rgba(0,217,163,0.4)' : 'none',
                          }}
                        />

                        {/* Content */}
                        <div className={`ml-10 md:ml-0 md:w-[45%] ${isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                          <div className={`bg-firmai-card border rounded-xl p-5 md:p-6 transition-all duration-300 cursor-pointer ${
                            isActive ? 'border-firmai-green/30 shadow-lg shadow-firmai-green/5' : 'border-firmai-border hover:border-firmai-green/20'
                          }`}>
                            <span className={`text-sm font-bold ${milestone.highlight ? 'text-firmai-green' : 'text-firmai-muted'}`}>
                              {milestone.year}
                            </span>
                            <h3 className="text-white font-semibold text-base mt-1">{milestone.title}</h3>
                            <p className="text-firmai-text text-sm mt-2 leading-relaxed">{milestone.description}</p>
                          </div>
                        </div>

                        {/* Spacer for opposite side */}
                        <div className="hidden md:block md:w-[45%]" />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 bg-firmai-dark">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,217,163,0.06)_0%,transparent_70%)]" />
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 max-w-2xl mx-auto">
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white">加入 Firmai</h2>
              <p className="text-firmai-text mt-4 text-base md:text-lg">
                我们正在寻找热爱能源科技与可持续发展的优秀人才
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                <button
                  onClick={() => navigate('/cases')}
                  className="inline-flex items-center gap-2 px-8 py-3.5 border border-white/30 text-white font-medium rounded-md hover:border-firmai-green hover:text-firmai-green transition-all duration-200 whitespace-nowrap group"
                >
                  查看客户案例
                  <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
                    <i className="ri-arrow-right-line" />
                  </span>
                </button>
                <a
                  href="mailto:info@xunyutech.cn"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line" />
                  </span>
                  联系我们
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
}